import { useRef, useEffect } from 'react';
import { View } from 'tamagui';
import config from '@/config/config';
import { PinPositionProps } from '@components/PinPosition/PinPosition';
import marker from '@assets/images/marker.png';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const DEFAULT_ZOOM = 14;
const DEFAULT_CENTER: [number, number] = [0, 0];

/**
 * PinPosition - Web implementation of map with center pin
 *
 * Features:
 * - Map that tracks center position
 * - Initializes with user's current location
 * - Static pin marker always at center of the map
 * - Calls onPositionChange when map center changes
 */
export default function PinPosition({
  onPositionChange,
}: PinPositionProps): React.ReactElement {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  const setToLocationToUsers = (): void => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // On success, center map on user's location
        const { latitude, longitude } = position.coords;
        if (mapRef.current) {
          mapRef.current.flyTo({
            center: [longitude, latitude],
            zoom: DEFAULT_ZOOM,
          });
        }
        onPositionChange({ latitude, longitude });
      },
      (error) => {
        // On error (permission denied or unavailable), stay at default
        console.warn('Geolocation error:', error.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  const initBrowserLocation = async (): Promise<void> => {
    try {
      const locationPermission = await navigator.permissions.query({
        name: 'geolocation',
      });

      if (locationPermission.state === 'denied') {
        // Permission denied, handle accordingly
        console.error('Location access denied by the user.');
        return;
      }
      setToLocationToUsers();
    } catch (error) {
      // Handle error
      console.error('Error checking location permission:', error);
    }
  };

  // Request geolocation permission immediately on mount to trigger browser popup
  useEffect(() => {
    if ('geolocation' in navigator) {
      initBrowserLocation();
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    mapboxgl.accessToken = config.mapbox.accessToken;

    // Create map with default center
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: config.mapbox.style,
    });

    // Add geolocate control
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
      showUserHeading: true,
    });
    mapRef.current.addControl(geolocate);

    // Update center coordinates on map move
    mapRef.current.on('move', () => {
      const center = mapRef.current?.getCenter();
      if (center) {
        onPositionChange({
          latitude: center.lat,
          longitude: center.lng,
        });
      }
    });

    // Set initial center from default
    onPositionChange({
      latitude: DEFAULT_CENTER[1],
      longitude: DEFAULT_CENTER[0],
    });

    return (): void => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <View position="relative" flex={1} width="100%" height="100%">
      {/* Map container */}
      <View ref={mapContainerRef} width="100%" height="100%" />

      {/* Center pin marker - positioned absolutely at center of map */}
      <View
        position="absolute"
        top="50%"
        left="50%"
        marginLeft={-20}
        marginTop={-40}
        zIndex={99}
        pointerEvents="none"
      >
        <img
          src={typeof marker === 'string' ? marker : marker.uri}
          alt="Pin marker"
          style={{ width: 40, height: 40, objectFit: 'contain' }}
        />
      </View>
    </View>
  );
}
